import {
  CoachingLogType,
  CoachingSessionTypeDTO,
} from '../../dtos/coaching-session.dto';

export interface SessionTypeLayoutOption {
  value: CoachingLogType;
  label: string;
  description: string;
}

export interface SessionTypeLayoutInfo {
  category: string;
  summary: string;
  example: string;
  reports: string[];
}

// The three layouts a session type can be created with. 'egrowe-coachlog-v2' is a
// legacy alias for 'mySmartCoach' logs) — it exists as data but
// isn't offered as a choice when creating a new type.
export const SESSION_TYPE_LAYOUT_OPTIONS: SessionTypeLayoutOption[] = [
  {
    value: 'general-log',
    label: 'General Session',
    description: 'Take notes, upload attachments, assign to dos',
  },
  {
    value: 'universal-log',
    label: 'Coaching Conversation',
    description: 'Choose Skills, Set Goals, Award Growth Indicators and Badges',
  },
  {
    value: 'observation',
    label: 'Observation and Feedback',
    description: 'Collect observable data in a form',
  },
];

export const SESSION_TYPE_LAYOUT_LABELS: Record<CoachingLogType, string> = {
  'general-log': 'General Session',
  'universal-log': 'Coaching Conversation',
  observation: 'Observation and Feedback',
  'egrowe-coachlog-v2': 'Coaching Conversation',
};

// Illustrated preview thumbnails per layout, matching the Figma prototype's
// LogPreviewModal. Layouts without an image yet fall back to a plain color
// block (see SessionTypePreviewModalComponent's preview-thumbnail).
export const SESSION_TYPE_LAYOUT_THUMBNAILS: Partial<
  Record<CoachingLogType, string>
> = {
  'universal-log': 'assets/session-types/layout-coaching.svg',
  'egrowe-coachlog-v2': 'assets/session-types/layout-coaching.svg',
  observation: 'assets/session-types/layout-observation.svg',
  'general-log': 'assets/session-types/layout-general.svg',
};

const COACHING_CONVERSATION_INFO: SessionTypeLayoutInfo = {
  category: 'Coaching Conversation',
  summary: 'Tracks growth and progress over time toward skill mastery.',
  example:
    "A coach works with a teacher over 7 sessions throughout the school year on specific goals they've set on skills or competencies.",
  reports: [
    'Coaching, Coaching Sessions, Coaching Conversations data',
    'Growth Indicator (GI), Badges, Microcredentials data',
    'Growth Rate and Skills Attained Data',
    'Coachee Confidence and Coaching Effectiveness',
    'Growth Report',
  ],
};

export const SESSION_TYPE_LAYOUT_INFO: Record<string, SessionTypeLayoutInfo> = {
  'universal-log': COACHING_CONVERSATION_INFO,
  'egrowe-coachlog-v2': COACHING_CONVERSATION_INFO,
  observation: {
    category: 'Observation and Feedback',
    summary:
      'Captures evidence of observable behaviors at a single point in time.',
    example:
      'An administrator visits a classroom and observes how often students engage in collaborative dialogue.',
    reports: ['Observations', 'Proforms'],
  },
  'general-log': {
    category: 'General Session',
    summary: 'Captures notes and action items for general meetings.',
    example: 'Data Meetings, PLCs, Admin Meetings, Debriefs, etc.',
    reports: ['Other Support Sessions', 'Support Insights', 'Session Details'],
  },
};

// mySmartCoach types (is_smart) still use one of the 4 layouts above under
// the hood (view/district_id/etc. are unaffected), but are shown with their
// own distinct label/thumbnail wherever a session type's layout is displayed
// — use these getters instead of indexing SESSION_TYPE_LAYOUT_LABELS/
// SESSION_TYPE_LAYOUT_THUMBNAILS directly so that override isn't missed.
const MY_SMART_COACH_LABEL = 'mySmartCoach';
const MY_SMART_COACH_THUMBNAIL = 'assets/session-types/layout-mysmartcoach.svg';
const MY_SMART_COACH_DESCRIPTION =
  'Structured support session for coaching cycles.';
const MY_SMART_COACH_INFO: SessionTypeLayoutInfo = {
  category: 'mySmartCoach',
  summary: 'Structured support session for coaching cycles.',
  example:
    'First- and second-year teachers receiving structured coaching support.',
  reports: ['Coaching Sessions', 'Support Insights'],
};

export const getSessionTypeLayoutLabel = (
  sessionType: Pick<CoachingSessionTypeDTO, 'view' | 'isSmart'>
): string =>
  sessionType.isSmart
    ? MY_SMART_COACH_LABEL
    : SESSION_TYPE_LAYOUT_LABELS[sessionType.view] || sessionType.view;

export const getSessionTypeLayoutThumbnail = (
  sessionType: Pick<CoachingSessionTypeDTO, 'view' | 'isSmart'>
): string | undefined =>
  sessionType.isSmart
    ? MY_SMART_COACH_THUMBNAIL
    : SESSION_TYPE_LAYOUT_THUMBNAILS[sessionType.view];

export const getSessionTypeLayoutDescription = (
  sessionType: Pick<CoachingSessionTypeDTO, 'view' | 'isSmart'>
): string | undefined =>
  sessionType.isSmart
    ? MY_SMART_COACH_DESCRIPTION
    : SESSION_TYPE_LAYOUT_OPTIONS.find(
        (option) => option.value === sessionType.view
      )?.description;

export const getSessionTypeLayoutInfo = (
  sessionType: Pick<CoachingSessionTypeDTO, 'view' | 'isSmart'>
): SessionTypeLayoutInfo | undefined =>
  sessionType.isSmart
    ? MY_SMART_COACH_INFO
    : SESSION_TYPE_LAYOUT_INFO[sessionType.view];
